import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { DomainConfigComponent } from '@app/domain-config.component';
import {
  Cara,
  CLAVE_CARA_GUARDADA,
  CLAVE_CARA_SESION,
  caraAlEntrar,
  entradaSuite,
  escribirFlujoClasico,
  escribirPanelClasico,
  PanelClasico,
  flujoDeRuta,
  leerSeleccionClasica,
  mismaFuncionalidad,
  panelClasicoDeRuta,
  SeleccionClasica,
  seleccionVigente,
  suiteUiActivo,
} from './suite-shell.logic';

/**
 * Cara activa de la app. El flag vive en el domain config ya cargado
 * (trackvision.suiteUi). El login vuelve a la clásica. El interruptor
 * no guarda preferencia: solo anota esta visita en sessionStorage.
 */
@Injectable({ providedIn: 'root' })
export class SuiteShellService {
  private readonly caraSubject = new BehaviorSubject<Cara>('classic');
  readonly cara$ = this.caraSubject.asObservable();
  private readonly listoSubject = new BehaviorSubject(false);
  readonly listo$ = this.listoSubject.asObservable();

  constructor(
    private domain: DomainConfigComponent,
    private router: Router,
  ) {
    this.cargar();
  }

  get cara(): Cara {
    return this.caraSubject.value;
  }

  get listo(): boolean {
    return this.listoSubject.value;
  }

  get muestraInterruptor(): boolean {
    return suiteUiActivo(this.domain.getConfigFlag('suiteUi'));
  }

  cargar(): void {
    this.olvidarPreferenciaGuardada();
    this.publicar(caraAlEntrar(
      this.domain.getConfigFlag('suiteUi'),
      this.leer(sessionStorage, CLAVE_CARA_SESION),
    ));
  }

  /** Cada inicio de sesión abre el intro clásico, sin arrastrar la suite de la visita anterior. */
  reiniciarEnClasica(): void {
    try {
      sessionStorage.removeItem(CLAVE_CARA_SESION);
    } catch {
      /* modo privado */
    }
    this.publicar('classic');
  }

  elegir(destino: Cara): void {
    if (!this.muestraInterruptor || destino === this.cara) return;
    if (destino === 'classic') this.guardarSeleccionDesdeSuite(this.router.url);
    const destinoUrl = destino === 'suite'
      ? this.urlEnSuite(this.router.url)
      : mismaFuncionalidad(destino, this.router.url, this.seleccionClasica());
    this.recordarSesion(destino);
    this.publicar(destino);
    this.router.navigateByUrl(destinoUrl);
  }

  /** Notificaciones (myactivities) del dominio: rige en las dos caras. */
  get tieneNotificaciones(): boolean {
    return this.domain.configMyActivities === true;
  }

  /** Inicio de la suite (logo del navbar). */
  get inicioSuite(): string {
    return entradaSuite(this.tieneNotificaciones);
  }

  /** La pantalla de la suite que corresponde a `url` y a lo que dejó la clásica. */
  urlEnSuite(url: string): string {
    const notificaciones = this.tieneNotificaciones;
    return mismaFuncionalidad(
      'suite',
      url,
      seleccionVigente(this.seleccionClasica(), notificaciones),
      entradaSuite(notificaciones),
    );
  }

  private guardarSeleccionDesdeSuite(url: string): void {
    const panel = panelClasicoDeRuta(url);
    const flujo = flujoDeRuta(url);
    if (panel) this.escribirPanel(panel);
    else if (flujo) this.escribirFlujo(flujo.grupo, flujo.visibilidad);
  }

  private seleccionClasica(): SeleccionClasica {
    return leerSeleccionClasica((clave) => this.leerTexto(clave));
  }

  private escribirFlujo(grupo: number, visibilidad: number): void {
    escribirFlujoClasico((clave, valor) => this.guardarTexto(clave, valor), grupo, visibilidad);
  }

  private escribirPanel(panel: PanelClasico | null): void {
    escribirPanelClasico((clave, valor) => this.guardarTexto(clave, valor), panel);
  }

  private guardarTexto(clave: string, valor: string | null): void {
    try {
      if (valor == null) localStorage.removeItem(clave);
      else localStorage.setItem(clave, valor);
    } catch {
      /* modo privado */
    }
  }

  private leerTexto(clave: string): string | null {
    try {
      return localStorage.getItem(clave);
    } catch {
      return null;
    }
  }

  private publicar(cara: Cara): void {
    this.caraSubject.next(cara);
    this.listoSubject.next(true);
  }

  private recordarSesion(cara: Cara): void {
    this.olvidarPreferenciaGuardada();
    try {
      sessionStorage.setItem(CLAVE_CARA_SESION, cara);
    } catch {
      /* modo privado: la cara vale igual durante esta visita */
    }
  }

  private olvidarPreferenciaGuardada(): void {
    try {
      localStorage.removeItem(CLAVE_CARA_GUARDADA);
    } catch {
      /* modo privado */
    }
  }

  private leer(storage: Storage, clave: string): Cara | null {
    try {
      const valor = storage.getItem(clave);
      if (valor === 'suite' || valor === 'classic') return valor;
    } catch {
      /* modo privado */
    }
    return null;
  }
}
